import { useState, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { Upload, Loader2 } from "lucide-react";
import { uploadFormData } from "@/api/client";

/**
 * Drag & drop multi-upload zone. Files land in the "general" folder. Zero-touch:
 * we send no `alts` — the server fills alt / title / description via AI so the
 * admin can drop and walk away. Manual edits happen later in MediaDetailDrawer.
 */
export default function UploadZone({ onUploaded }: { onUploaded: () => void }) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useMutation({
    mutationFn: async (fileList: FileList) => {
      const formData = new FormData();
      for (let i = 0; i < fileList.length; i++) {
        formData.append("files", fileList[i]);
      }
      formData.append("folder", "general");
      return uploadFormData("/api/admin/upload/multiple", formData);
    },
    onSuccess: () => {
      setUploadError(null);
      onUploaded();
    },
    onError: (err) => setUploadError(err instanceof Error ? err.message : "Upload failed"),
  });

  return (
    <div className="space-y-2">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          if (e.dataTransfer.files?.length) uploadMutation.mutate(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 text-gray-500 transition-colors ${
          isDragging
            ? "border-primary-500 bg-primary-50 dark:bg-primary-900/10"
            : "border-gray-300 hover:border-gray-400 dark:border-gray-600"
        }`}
      >
        {uploadMutation.isPending ? (
          <>
            <Loader2 className="mb-2 h-8 w-8 animate-spin" />
            <p className="text-sm">Uploading & processing…</p>
          </>
        ) : (
          <>
            <Upload className="mb-2 h-8 w-8" />
            <p className="text-sm">Drop images here or click to browse</p>
            <p className="text-xs text-gray-400">
              Files go to the “general” folder — move them after upload.
            </p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => e.target.files?.length && uploadMutation.mutate(e.target.files)}
        />
      </div>
      {uploadError && (
        <div className="bg-error-50 text-error-700 dark:bg-error-900/20 dark:text-error-400 rounded-lg p-2 text-sm">
          {uploadError}
        </div>
      )}
    </div>
  );
}
